import { ChildDetailRow, CityNightRow, createDefaultCityRow } from "component/quotation/QuotationBasicDetails";
import React, { useState, createContext, Dispatch, ReactNode, SetStateAction } from "react";
import {
    JourneyDayItem,
    AdditionalCostItem,
    CategoryBreakdown,
    PricingMarkupItem,
    TripOptionItem,
    ExcursionOptionItem,
    QuotationHotelOptionsByDateHotel, QuotationDetailsResponseModel,
    GenericQuotationFollowUpTypeItem,
} from "../models/axiosModel";
import {IGuideGroupPricing, IQuotationStatus} from "models";

interface QuotationProviderProps {
    children: ReactNode;
}

export interface SelectedCities {
    city_id: number;
    number_of_nights: number;
    quotation_city_id?: string;
    sequence_number?: number;
    city_name?: string;
}

export interface DropdownOption {
    label: string;
    value: string;
}

export interface PricingTotals {
    subtotal_usd: number;
    total_markup_usd: number;
    final_total_usd: number;
}

export interface JourneyOptionsByDateValue {
    tours: TripOptionItem[];
    transfers: TripOptionItem[];
    excursions: ExcursionOptionItem[];
}

export const buildCityDateKey = (cityId: number | string | undefined | null, date: string | undefined | null): string =>
    `${cityId ?? ""}__${date ?? ""}`;

export const createEmptyPricingTotals = (): PricingTotals => ({
    subtotal_usd: 0,
    total_markup_usd: 0,
    final_total_usd: 0,
});

export interface QuotationContextType {
    quotationId: string;
    setQuotationId: Dispatch<SetStateAction<string>>;
    quotationName: string;
    setQuotationName: Dispatch<SetStateAction<string>>;
    quotationStatusOptions: IQuotationStatus[];
    setQuotationStatusOptions: Dispatch<SetStateAction<IQuotationStatus[]>>;
    quotationFollowUpTypeOptions: GenericQuotationFollowUpTypeItem[];
    setQuotationFollowUpTypeOptions: Dispatch<SetStateAction<GenericQuotationFollowUpTypeItem[]>>;
    staCode: string;
    setStaCode: Dispatch<SetStateAction<string>>;
    createdBy: string;
    setCreatedBy: Dispatch<SetStateAction<string>>;
    agencyId: string;
    setAgencyId: Dispatch<SetStateAction<string>>;
    agentId: string;
    setAgentId: Dispatch<SetStateAction<string>>;
    startDate: string;
    setStartDate: Dispatch<SetStateAction<string>>;
    noOfNights: string;
    setNoOfNights: Dispatch<SetStateAction<string>>;
    selectedCities: CityNightRow[];
    setSelectedCities: Dispatch<SetStateAction<CityNightRow[]>>;
    adults: string;
    setAdults: Dispatch<SetStateAction<string>>;
    children: string;
    setChildren: Dispatch<SetStateAction<string>>;
    extraBed: string;
    setExtraBed: Dispatch<SetStateAction<string>>;
    childDetails: ChildDetailRow[];
    setChildDetails: Dispatch<SetStateAction<ChildDetailRow[]>>;
    rooms: string;
    setRooms: Dispatch<SetStateAction<string>>;
    flagVisaFee: boolean;
    setFlagVisaFee: Dispatch<SetStateAction<boolean>>;
    visaFeePerPax: string;
    setVisaFeePerPax: Dispatch<SetStateAction<string>>;
    foc: boolean;
    setFoc: Dispatch<SetStateAction<boolean>>;
    focPaxNumber: string;
    setFocPaxNumber: Dispatch<SetStateAction<string>>;
    quotationData: QuotationDetailsResponseModel | null;
    setQuotationData: Dispatch<SetStateAction<QuotationDetailsResponseModel | null>>;
    currentQuotationStatusObj: IQuotationStatus | null;
    setCurrentQuotationStatusObj: Dispatch<SetStateAction<IQuotationStatus | null>>;

    // ---------- Cross-step shared dropdown options ----------
    cityOptions: DropdownOption[];
    setCityOptions: Dispatch<SetStateAction<DropdownOption[]>>;

    // ---------- Core fields edited in Step 2/3 but stored on quotation core ----------
    flagWithTip: boolean;
    setFlagWithTip: Dispatch<SetStateAction<boolean>>;
    packageServiceCharge: string;
    setPackageServiceCharge: Dispatch<SetStateAction<string>>;
    discountUsd: string;
    setDiscountUsd: Dispatch<SetStateAction<string>>;

    // ---------- Step 2 — Journey Details ----------
    journeyDays: JourneyDayItem[];
    setJourneyDays: Dispatch<SetStateAction<JourneyDayItem[]>>;
    additionalCosts: AdditionalCostItem[];
    setAdditionalCosts: Dispatch<SetStateAction<AdditionalCostItem[]>>;

    // Lazy caches keyed by `${city_id}__${date}` (see buildCityDateKey).
    hotelOptionsByCityDate: Record<string, QuotationHotelOptionsByDateHotel[]>;
    setHotelOptionsByCityDate: Dispatch<SetStateAction<Record<string, QuotationHotelOptionsByDateHotel[]>>>;
    journeyOptionsByCityDate: Record<string, JourneyOptionsByDateValue>;
    setJourneyOptionsByCityDate: Dispatch<SetStateAction<Record<string, JourneyOptionsByDateValue>>>;

    // ---------- Step 3 — Pricing Summary ----------
    pricingCategoryBreakdown: CategoryBreakdown[];
    setPricingCategoryBreakdown: Dispatch<SetStateAction<CategoryBreakdown[]>>;
    pricingMarkups: PricingMarkupItem[];
    setPricingMarkups: Dispatch<SetStateAction<PricingMarkupItem[]>>;
    pricingTotals: PricingTotals;
    setPricingTotals: Dispatch<SetStateAction<PricingTotals>>;

    quotationBasicDetailsApiResponse: any;
    setQuotationBasicDetailsApiResponse: Dispatch<SetStateAction<any>>;

    guideGroupPricing: IGuideGroupPricing | null;
    setGuideGroupPricing: Dispatch<SetStateAction<IGuideGroupPricing | null>>;

    resetQuotationContext: () => void;
}

const QuotationContext = createContext<QuotationContextType | undefined>(undefined);

const QuotationProvider = (props: QuotationProviderProps) => {
    const [quotationId, setQuotationId] = useState<string>("");
    const [quotationName, setQuotationName] = useState<string>("");
    const [quotationStatusOptions, setQuotationStatusOptions] = useState<IQuotationStatus[]>([]);
    const [quotationFollowUpTypeOptions, setQuotationFollowUpTypeOptions] = useState<GenericQuotationFollowUpTypeItem[]>([]);
    const [staCode, setStaCode] = useState<string>("");
    const [createdBy, setCreatedBy] = useState<string>("");
    const [agencyId, setAgencyId] = useState<string>("");
    const [agentId, setAgentId] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [noOfNights, setNoOfNights] = useState<string>("1");
    const [selectedCities, setSelectedCities] = useState<CityNightRow[]>([createDefaultCityRow()]);
    const [adults, setAdults] = useState<string>("2");
    const [children, setChildren] = useState<string>("0");
    const [extraBed, setExtraBed] = useState<string>("0");
    const [childDetails, setChildDetails] = useState<ChildDetailRow[]>([]);
    const [rooms, setRooms] = useState<string>("1");
    const [flagVisaFee, setFlagVisaFee] = useState<boolean>(false);
    const [visaFeePerPax, setVisaFeePerPax] = useState<string>("750");
    const [foc, setFoc] = useState<boolean>(false);
    const [focPaxNumber, setFocPaxNumber] = useState<string>("0");
    const [quotationData, setQuotationData] = useState<QuotationDetailsResponseModel | null>(null);
    const [currentQuotationStatusObj, setCurrentQuotationStatusObj] = useState<IQuotationStatus | null>(null);

    const [cityOptions, setCityOptions] = useState<DropdownOption[]>([]);

    const [flagWithTip, setFlagWithTip] = useState<boolean>(true);
    const [packageServiceCharge, setPackageServiceCharge] = useState<string>("0");
    const [discountUsd, setDiscountUsd] = useState<string>("0");

    const [journeyDays, setJourneyDays] = useState<JourneyDayItem[]>([]);
    const [additionalCosts, setAdditionalCosts] = useState<AdditionalCostItem[]>([]);
    const [hotelOptionsByCityDate, setHotelOptionsByCityDate] = useState<Record<string, QuotationHotelOptionsByDateHotel[]>>({});
    const [journeyOptionsByCityDate, setJourneyOptionsByCityDate] = useState<Record<string, JourneyOptionsByDateValue>>({});

    const [pricingCategoryBreakdown, setPricingCategoryBreakdown] = useState<CategoryBreakdown[]>([]);
    const [pricingMarkups, setPricingMarkups] = useState<PricingMarkupItem[]>([]);
    const [pricingTotals, setPricingTotals] = useState<PricingTotals>(createEmptyPricingTotals());
    const [quotationBasicDetailsApiResponse, setQuotationBasicDetailsApiResponse] = useState<any>(null);
    const [guideGroupPricing, setGuideGroupPricing] = useState<IGuideGroupPricing | null>(null);

    const resetQuotationContext = () => {
        setQuotationId("");
        setQuotationName("");
        setStaCode("");
        setCreatedBy("");
        setAgencyId("");
        setAgentId("");
        setStartDate("");
        setNoOfNights("1");
        setSelectedCities([createDefaultCityRow()]);
        setAdults("2");
        setChildren("0");
        setExtraBed("0");
        setChildDetails([]);
        setRooms("1");
        setFlagVisaFee(false);
        setVisaFeePerPax("750");
        setFoc(false);
        setFocPaxNumber("0");
        setCityOptions([]);
        setFlagWithTip(true);
        setPackageServiceCharge("0");
        setDiscountUsd("0");
        setJourneyDays([]);
        setAdditionalCosts([]);
        setHotelOptionsByCityDate({});
        setJourneyOptionsByCityDate({});
        setPricingCategoryBreakdown([]);
        setPricingMarkups([]);
        setPricingTotals(createEmptyPricingTotals());
    };

    const payload = {
        quotationId,
        setQuotationId,
        quotationName,
        setQuotationName,
        quotationStatusOptions,
        setQuotationStatusOptions,
        quotationFollowUpTypeOptions,
        setQuotationFollowUpTypeOptions,
        staCode,
        setStaCode,
        createdBy,
        setCreatedBy,
        agencyId,
        setAgencyId,
        agentId,
        setAgentId,
        startDate,
        setStartDate,
        noOfNights,
        setNoOfNights,
        selectedCities,
        setSelectedCities,
        adults,
        setAdults,
        children,
        setChildren,
        extraBed,
        setExtraBed,
        childDetails,
        setChildDetails,
        rooms,
        setRooms,
        flagVisaFee,
        setFlagVisaFee,
        visaFeePerPax,
        setVisaFeePerPax,
        foc,
        setFoc,
        focPaxNumber,
        setFocPaxNumber,
        quotationData,
        setQuotationData,
        currentQuotationStatusObj,
        setCurrentQuotationStatusObj,

        cityOptions,
        setCityOptions,

        flagWithTip,
        setFlagWithTip,
        packageServiceCharge,
        setPackageServiceCharge,
        discountUsd,
        setDiscountUsd,

        journeyDays,
        setJourneyDays,
        additionalCosts,
        setAdditionalCosts,
        hotelOptionsByCityDate,
        setHotelOptionsByCityDate,
        journeyOptionsByCityDate,
        setJourneyOptionsByCityDate,

        pricingCategoryBreakdown,
        setPricingCategoryBreakdown,
        pricingMarkups,
        setPricingMarkups,
        pricingTotals,
        setPricingTotals,
        quotationBasicDetailsApiResponse, 
        setQuotationBasicDetailsApiResponse,

        guideGroupPricing,
        setGuideGroupPricing,

        resetQuotationContext,
    };

    return (
        <QuotationContext.Provider value={payload}>
            {props.children}
        </QuotationContext.Provider>
    );
};

const useQuotation = (): QuotationContextType => {
    const context = React.useContext<QuotationContextType | undefined>(QuotationContext);
    if (!context) {
        throw new Error('useQuotation must be used within a QuotationProvider');
    }
    return context as QuotationContextType;
};

export { QuotationProvider, QuotationContext, useQuotation };

